<script setup>
import { ref } from 'vue'
import { useForm, Link, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import HostPortalLayout from '@/Layouts/HostPortalLayout.vue'
import LocationPickerModal from '@/Components/UI/LocationPickerModal.vue'

defineOptions({ layout: HostPortalLayout })

const { t } = useI18n()

const props = defineProps({
  property:      { type: Object, required: true },
  errors:        { type: Object, default: () => ({}) },
  categories:    { type: Array,  default: () => [] },
  statuses:      { type: Array,  default: () => [] },
  amenities:     { type: Array,  default: () => [] },
  host:          { type: Object, required: true },
  currency_code: { type: String, default: 'THB' },
  // Display-currency overrides — controller passes the enabled
  // non-canonical currencies + live FX rates so the form can render
  // auto-converted previews next to each "Set own price" checkbox.
  display_currencies: { type: Array,  default: () => [] },
  fx_margin_pct:      { type: Number, default: 1.0 },
  fx_rates:           { type: Object, default: () => ({}) }
})

const FORMATS = {
  THB: { symbol: '฿', decimals: 2 },
  USD: { symbol: '$', decimals: 2 },
  RUB: { symbol: '₽', decimals: 2 },
  CNY: { symbol: '¥', decimals: 2 }
}

const isEdit = !!props.property.id

const form = useForm({
  title:                 props.property.title ?? '',
  summary:               props.property.summary ?? '',
  description:           props.property.description ?? '',
  category:              props.property.category ?? 'apartment',
  area:                  props.property.area ?? '',
  address:               props.property.address ?? '',
  lat:                   props.property.lat ?? '',
  lng:                   props.property.lng ?? '',
  bedrooms:              props.property.bedrooms ?? '',
  bathrooms:             props.property.bathrooms ?? '',
  max_guests:            props.property.max_guests ?? '',
  size_sqm:              props.property.size_sqm ?? '',
  price_per_night_cents: props.property.price_per_night_cents ?? null,
  price_per_month_cents: props.property.price_per_month_cents ?? null,
  status:                props.property.status ?? 'active',
  amenities:             props.property.amenities ?? [],
  photos:                []
})

// ── Per-currency display-price overrides ──
const overrideState = ref({})
props.display_currencies.forEach(code => {
  const existing = (props.property.property_prices || []).find(p => p.currency_code === code)
  overrideState.value[code] = {
    id:                    existing?.id || null,
    night_enabled:         existing && existing.price_per_night_cents != null,
    month_enabled:         existing && existing.price_per_month_cents != null,
    price_per_night_cents: existing?.price_per_night_cents ?? null,
    price_per_month_cents: existing?.price_per_month_cents ?? null
  }
})

function autoConverted(field, code) {
  const rate = props.fx_rates[code]
  if (!rate) return null
  const canonicalCents = form[field] || 0
  const margin = 1 + (props.fx_margin_pct / 100.0)
  return Math.round(canonicalCents * rate * margin)
}

function formatCents(cents, code) {
  if (cents == null) return '—'
  const fmt = FORMATS[code] || { symbol: code, decimals: 2 }
  return `${fmt.symbol}${(cents / 100).toFixed(fmt.decimals)}`
}

function inputCurrencyFromCents(cents) {
  if (cents == null) return ''
  return (cents / 100).toString()
}

function inputCurrencyToCents(input) {
  const num = parseFloat(input)
  if (Number.isNaN(num)) return null
  return Math.round(num * 100)
}

function toggleAmenity(a, checked) {
  const set = new Set(form.amenities)
  if (checked) set.add(a)
  else set.delete(a)
  form.amenities = Array.from(set)
}

function buildFormData() {
  const fd = new FormData()
  const fields = ['title', 'summary', 'description', 'category', 'area',
                  'address', 'lat', 'lng', 'bedrooms', 'bathrooms',
                  'max_guests', 'size_sqm', 'price_per_night_cents',
                  'price_per_month_cents', 'status']
  fields.forEach(k => fd.append(`property[${k}]`, form[k] ?? ''))
  form.photos.forEach(file => fd.append('property[photos][]', file))

  // Amenities — selected as array; empty sentinel so Rails clears it.
  if (form.amenities.length > 0) {
    form.amenities.forEach(a => fd.append('property[amenities][]', a))
  } else {
    fd.append('property[amenities][]', '')
  }

  // Per-currency override rows.
  props.display_currencies.forEach((code, idx) => {
    const s = overrideState.value[code]
    if (!s) return
    const hasAnyEnabled = s.night_enabled || s.month_enabled
    if (s.id && !hasAnyEnabled) {
      fd.append(`property[property_prices_attributes][${idx}][id]`, s.id)
      fd.append(`property[property_prices_attributes][${idx}][_destroy]`, '1')
    } else if (hasAnyEnabled) {
      if (s.id) fd.append(`property[property_prices_attributes][${idx}][id]`, s.id)
      fd.append(`property[property_prices_attributes][${idx}][currency_code]`, code)
      fd.append(`property[property_prices_attributes][${idx}][price_per_night_cents]`,
                s.night_enabled && s.price_per_night_cents != null ? s.price_per_night_cents : '')
      fd.append(`property[property_prices_attributes][${idx}][price_per_month_cents]`,
                s.month_enabled && s.price_per_month_cents != null ? s.price_per_month_cents : '')
    }
  })

  return fd
}

function submit() {
  const fd = buildFormData()
  const opts = { onError: (errors) => { form.errors = errors }, preserveScroll: true }
  if (isEdit) {
    fd.append('_method', 'PATCH')
    router.post(`/properties/${props.property.id}`, fd, opts)
  } else {
    router.post('/properties', fd, opts)
  }
}

function onPhotos(e) {
  form.photos = Array.from(e.target.files || [])
}

function err(field) {
  return form.errors[field] || props.errors[field]?.[0]
}

function centsToInput(cents) {
  if (cents == null) return ''
  const v = cents / 100
  return Number.isInteger(v) ? String(v) : v.toFixed(2)
}

const nightlyPriceDollars = {
  get: () => centsToInput(form.price_per_night_cents),
  set: (v) => {
    const trimmed = String(v).trim()
    form.price_per_night_cents = trimmed === '' ? null : (Math.round(parseFloat(v) * 100) || 0)
  }
}
const monthlyPriceDollars = {
  get: () => centsToInput(form.price_per_month_cents),
  set: (v) => {
    const trimmed = String(v).trim()
    form.price_per_month_cents = trimmed === '' ? null : (Math.round(parseFloat(v) * 100) || 0)
  }
}

const inputBase = 'w-full rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]'
const labelCls  = 'block text-sm font-medium text-[color:var(--inkSoft)] mb-1'
const errorCls  = 'mt-1 text-xs text-[color:var(--danger)]'

// Location map picker — shared LocationPickerModal (Leaflet +
// Nominatim) drops a pin, reverse-geocodes, and emits { address, lat,
// lng } on save. We persist all three onto the property record.
const locationPickerOpen = ref(false)
function onLocationSaved(payload) {
  form.address = payload.address ?? form.address
  form.lat     = payload.lat     ?? form.lat
  form.lng     = payload.lng     ?? form.lng
  locationPickerOpen.value = false
}
function clearLocation() {
  form.address = ''
  form.lat     = ''
  form.lng     = ''
}
</script>

<template>
  <Link
    :href="isEdit ? `/properties/${property.id}` : '/'"
    class="back"
  >
    ← {{ t('host_portal.property_form.back') }}
  </Link>
  <h1 class="title">
    {{ isEdit ? t('host_portal.property_form.title_edit') : t('host_portal.property_form.title') }}
  </h1>
  <p class="subtitle">
    {{ t('host_portal.property_form.subtitle', { host: host.name }) }}
  </p>

  <div
    v-if="property.approval_state"
    class="approval-pill"
    :class="`approval-pill--${property.approval_state}`"
  >
    {{ t(`host_portal.property.approval.${property.approval_state}`) }}
    <span
      v-if="property.rejection_reason"
      class="approval-pill__reason"
    >— {{ property.rejection_reason }}</span>
  </div>

  <form
    id="property-form"
    class="card"
    @submit.prevent="submit"
  >
    <div>
      <label :class="labelCls">{{ t('host_portal.property_form.fields.title') }} *</label>
      <input
        v-model="form.title"
        type="text"
        required
        :class="[inputBase, err('title') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']"
      >
      <p
        v-if="err('title')"
        :class="errorCls"
      >
        {{ err('title') }}
      </p>
    </div>

    <div>
      <label :class="labelCls">{{ t('host_portal.property_form.fields.summary') }}</label>
      <input
        v-model="form.summary"
        type="text"
        maxlength="200"
        :class="[inputBase, 'border-[color:var(--line)]']"
      >
      <p class="hint">
        {{ t('host_portal.property_form.summary_hint') }}
      </p>
    </div>

    <div>
      <label :class="labelCls">{{ t('host_portal.property_form.fields.description') }}</label>
      <textarea
        v-model="form.description"
        rows="5"
        :class="[inputBase, 'border-[color:var(--line)]']"
      />
    </div>

    <div class="grid2">
      <div>
        <label :class="labelCls">{{ t('host_portal.property_form.fields.category') }}</label>
        <select
          v-model="form.category"
          :class="[inputBase, 'border-[color:var(--line)] capitalize']"
        >
          <option
            v-for="c in categories"
            :key="c"
            :value="c"
          >
            {{ t(`host_portal.property.categories.${c}`) }}
          </option>
        </select>
      </div>
      <div>
        <label :class="labelCls">{{ t('host_portal.property_form.fields.area') }}</label>
        <input
          v-model="form.area"
          type="text"
          :class="[inputBase, 'border-[color:var(--line)]']"
        >
      </div>
    </div>

    <div class="grid2">
      <div>
        <label :class="labelCls">{{ t('host_portal.property_form.fields.bedrooms') }}</label>
        <input
          v-model.number="form.bedrooms"
          type="number"
          min="0"
          :class="[inputBase, 'border-[color:var(--line)]']"
        >
      </div>
      <div>
        <label :class="labelCls">{{ t('host_portal.property_form.fields.bathrooms') }}</label>
        <input
          v-model.number="form.bathrooms"
          type="number"
          min="0"
          :class="[inputBase, 'border-[color:var(--line)]']"
        >
      </div>
    </div>

    <div class="grid2">
      <div>
        <label :class="labelCls">{{ t('host_portal.property_form.fields.max_guests') }}</label>
        <input
          v-model.number="form.max_guests"
          type="number"
          min="0"
          :class="[inputBase, 'border-[color:var(--line)]']"
        >
      </div>
      <div>
        <label :class="labelCls">{{ t('host_portal.property_form.fields.size_sqm') }}</label>
        <input
          v-model.number="form.size_sqm"
          type="number"
          min="0"
          :class="[inputBase, 'border-[color:var(--line)]']"
        >
      </div>
    </div>

    <div class="grid2">
      <div>
        <label :class="labelCls">{{ t('host_portal.property_form.fields.price_per_night') }} ({{ currency_code }})</label>
        <input
          :value="nightlyPriceDollars.get()"
          type="number"
          min="0"
          step="1"
          :class="[inputBase, 'border-[color:var(--line)]']"
          @input="nightlyPriceDollars.set($event.target.value)"
        >
      </div>
      <div>
        <label :class="labelCls">{{ t('host_portal.property_form.fields.price_per_month') }} ({{ currency_code }})</label>
        <input
          :value="monthlyPriceDollars.get()"
          type="number"
          min="0"
          step="1"
          :class="[inputBase, 'border-[color:var(--line)]']"
          @input="monthlyPriceDollars.set($event.target.value)"
        >
        <p class="hint">
          {{ t('host_portal.property_form.price_hint') }}
        </p>
      </div>
    </div>

    <!-- Per-currency display-price overrides — public catalog only -->
    <div v-if="display_currencies.length > 0" class="override-card">
      <h3 class="override-title">{{ t('host_portal.property_form.fields.display_overrides') }}</h3>
      <p class="hint">{{ t('host_portal.property_form.fields.display_overrides_hint') }}</p>
      <div v-for="code in display_currencies" :key="code" class="override-row">
        <div class="override-code">{{ code }}</div>
        <div class="grid2">
          <div>
            <label class="override-toggle">
              <input
                type="checkbox"
                :checked="overrideState[code]?.night_enabled"
                @change="overrideState[code].night_enabled = $event.target.checked; if (!$event.target.checked) overrideState[code].price_per_night_cents = null"
              >
              <span>{{ t('host_portal.property_form.fields.set_own_night') }}</span>
            </label>
            <div v-if="overrideState[code]?.night_enabled">
              <input
                type="number" min="0" step="1"
                :value="inputCurrencyFromCents(overrideState[code].price_per_night_cents)"
                :class="[inputBase, 'border-[color:var(--line)]']"
                :placeholder="`${FORMATS[code]?.symbol || ''}…`"
                @input="overrideState[code].price_per_night_cents = inputCurrencyToCents($event.target.value)"
              >
            </div>
            <div v-else class="override-preview">
              {{ t('host_portal.property_form.fields.auto_converted') }}: {{ formatCents(autoConverted('price_per_night_cents', code), code) }}
            </div>
          </div>
          <div>
            <label class="override-toggle">
              <input
                type="checkbox"
                :checked="overrideState[code]?.month_enabled"
                @change="overrideState[code].month_enabled = $event.target.checked; if (!$event.target.checked) overrideState[code].price_per_month_cents = null"
              >
              <span>{{ t('host_portal.property_form.fields.set_own_month') }}</span>
            </label>
            <div v-if="overrideState[code]?.month_enabled">
              <input
                type="number" min="0" step="1"
                :value="inputCurrencyFromCents(overrideState[code].price_per_month_cents)"
                :class="[inputBase, 'border-[color:var(--line)]']"
                :placeholder="`${FORMATS[code]?.symbol || ''}…`"
                @input="overrideState[code].price_per_month_cents = inputCurrencyToCents($event.target.value)"
              >
            </div>
            <div v-else class="override-preview">
              {{ t('host_portal.property_form.fields.auto_converted') }}: {{ formatCents(autoConverted('price_per_month_cents', code), code) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div>
      <label :class="labelCls">{{ t('host_portal.property_form.fields.amenities') }}</label>
      <div class="amenities-grid">
        <label
          v-for="a in amenities"
          :key="a"
          class="amenity-toggle"
        >
          <input
            type="checkbox"
            :checked="form.amenities.includes(a)"
            @change="toggleAmenity(a, $event.target.checked)"
          >
          <span>{{ t(`host_portal.property.amenities.${a}`) }}</span>
        </label>
      </div>
    </div>

    <div class="location-card">
      <label :class="labelCls">{{ t('host_portal.property_form.fields.location') }}</label>
      <div class="location-row">
        <div class="location-row__main">
          <p v-if="form.address" class="location-address">
            {{ form.address }}
          </p>
          <p v-else class="location-empty">
            {{ t('host_portal.property_form.location_empty') }}
          </p>
          <p v-if="form.lat && form.lng" class="location-coords">
            {{ Number(form.lat).toFixed(5) }}, {{ Number(form.lng).toFixed(5) }}
          </p>
        </div>
        <div class="location-actions">
          <button type="button" class="btn" @click="locationPickerOpen = true">
            {{ form.address ? t('host_portal.property_form.location_edit') : t('host_portal.property_form.location_set') }}
          </button>
          <button v-if="form.address" type="button" class="btn btn--ghost" @click="clearLocation">
            {{ t('host_portal.property_form.location_clear') }}
          </button>
        </div>
      </div>
    </div>

    <div>
      <label :class="labelCls">{{ t('host_portal.property_form.fields.photos') }}</label>
      <input
        type="file"
        accept="image/*"
        multiple
        class="w-full text-sm text-[color:var(--inkSoft)] file:mr-3 file:py-1.5 file:px-3 file:rounded-[var(--radius-md)] file:border-0 file:text-sm file:font-medium file:bg-[color:var(--accentSoft)] file:text-[color:var(--accent)] hover:file:brightness-95 file:cursor-pointer"
        @change="onPhotos"
      >
      <p class="hint">
        {{ t('host_portal.property_form.photos_hint') }}
      </p>
    </div>

    <div>
      <label :class="labelCls">{{ t('host_portal.property_form.fields.status') }}</label>
      <select
        v-model="form.status"
        :class="[inputBase, 'border-[color:var(--line)]']"
      >
        <option
          v-for="s in statuses"
          :key="s"
          :value="s"
        >
          {{ t(`host_portal.property.status.${s}`) }}
        </option>
      </select>
    </div>

    <div
      v-if="errors.base"
      class="errors"
    >
      <p
        v-for="(m, i) in (Array.isArray(errors.base) ? errors.base : [errors.base])"
        :key="i"
      >
        {{ m }}
      </p>
    </div>

    <div class="actions">
      <Link
        href="/"
        class="btn"
      >
        {{ t('host_portal.property_form.cancel') }}
      </Link>
      <button
        type="submit"
        class="btn btn--primary"
        :disabled="form.processing"
      >
        {{ form.processing ? t('host_portal.property_form.submitting') : t('host_portal.property_form.submit') }}
      </button>
    </div>
  </form>

  <LocationPickerModal
    :open="locationPickerOpen"
    :title="t('host_portal.property_form.location_modal_title')"
    :address="form.address"
    :lat="form.lat || null"
    :lng="form.lng || null"
    @save="onLocationSaved"
    @close="locationPickerOpen = false"
  />
</template>

<style scoped>
.back     { color: var(--muted); text-decoration: none; font-size: 0.875rem; }
.title    { font-family: var(--font-display, var(--display)); font-size: 1.5rem; color: var(--ink); margin: 0.5rem 0 0.25rem; }
.subtitle { color: var(--inkSoft); margin: 0 0 1rem; font-size: 0.9375rem; }
.approval-pill { display: inline-block; padding: 0.4rem 0.75rem; margin: 0 0 1rem; border-radius: 999px; font-size: 0.8125rem; font-weight: 500; }
.approval-pill--pending  { background: color-mix(in oklch, var(--warn) 16%, var(--surface)); color: var(--warn); }
.approval-pill--approved { background: color-mix(in oklch, var(--ok) 16%, var(--surface)); color: var(--ok); }
.approval-pill--rejected { background: color-mix(in oklch, var(--danger) 14%, var(--surface)); color: var(--danger); }
.approval-pill__reason   { font-weight: 400; }

.card { background: var(--surface); border: 1px solid var(--line); border-radius: 0.75rem; padding: 1.25rem 1.5rem; display: grid; gap: 1.25rem; max-width: 48rem; }
.grid2 { display: grid; grid-template-columns: 1fr; gap: 1rem; }
@media (min-width: 640px) { .grid2 { grid-template-columns: 1fr 1fr; } }
.hint { margin: 0.25rem 0 0; font-size: 0.75rem; color: var(--muted); }
.errors { background: color-mix(in oklch, var(--danger) 12%, var(--surface)); color: var(--danger); padding: 0.75rem 1rem; border-radius: 0.5rem; font-size: 0.875rem; }
.errors p { margin: 0; }
.actions { display: flex; gap: 0.5rem; justify-content: flex-end; }
.btn { padding: 0.55rem 1rem; border: 1px solid var(--line); background: var(--surface); color: var(--ink); border-radius: var(--radius-button, 8px); text-decoration: none; font-weight: 500; cursor: pointer; }
.btn--primary { background: var(--accent); color: var(--accentInk); border-color: var(--accent); }
.btn--primary:disabled { opacity: 0.5; cursor: not-allowed; }
.override-card {
  border: 1px solid var(--line); border-radius: 0.75rem;
  padding: 1rem 1.25rem; display: grid; gap: 1rem;
  background: var(--surface);
}
.override-title { margin: 0; font-size: 0.875rem; font-weight: 600; color: var(--ink); }
.override-row { padding-top: 0.75rem; border-top: 1px solid var(--line); display: grid; gap: 0.5rem; }
.override-row:first-of-type { padding-top: 0; border-top: 0; }
.override-code { font-size: 0.875rem; font-weight: 600; color: var(--inkSoft); }
.override-toggle { display: flex; align-items: center; gap: 0.4rem; font-size: 0.8125rem; color: var(--inkSoft); margin-bottom: 0.25rem; cursor: pointer; }
.override-preview { font-size: 0.75rem; color: var(--muted); font-style: italic; padding-top: 0.25rem; }
.amenities-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.4rem 1rem; }
@media (min-width: 640px) { .amenities-grid { grid-template-columns: repeat(3, 1fr); } }
.amenity-toggle { display: flex; align-items: center; gap: 0.4rem; font-size: 0.8125rem; color: var(--inkSoft); cursor: pointer; }
.location-card {
  border: 1px solid var(--line); border-radius: 0.5rem;
  padding: 0.75rem 0.875rem; background: var(--surface);
}
.location-row { display: flex; align-items: flex-start; gap: 0.75rem; }
.location-row__main { flex: 1; min-width: 0; }
.location-address { margin: 0; color: var(--ink); font-size: 0.9375rem; }
.location-coords { margin: 0.2rem 0 0; color: var(--muted); font-size: 0.75rem; font-family: var(--font-mono, monospace); }
.location-empty { margin: 0; color: var(--muted); font-size: 0.875rem; font-style: italic; }
.location-actions { display: flex; flex-direction: column; gap: 0.35rem; align-items: stretch; min-width: 8rem; }
.btn--ghost { background: transparent; border-color: var(--line); color: var(--muted); }
.btn--ghost:hover { color: var(--ink); }
</style>
